import { useState, useEffect } from 'react';

export type ViewMode = 'split' | 'unified';

const STORAGE_KEY = 'diff-viewer-mode';

/**
 * Custom hook for managing view mode state with sessionStorage persistence.
 *
 * Persists the user's diff viewer preference (split vs unified) across page navigations
 * within the same browser session. Storage operations are wrapped in try/catch to handle
 * private browsing mode or other storage restrictions gracefully.
 *
 * @param defaultMode - The default view mode to use if no stored preference exists
 * @returns Tuple of [currentMode, setMode] for reading and updating the view mode
 */
export function useViewModePersistence(
  defaultMode: ViewMode = 'split'
): [ViewMode, (mode: ViewMode) => void] {
  // Lazy initialize from sessionStorage
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      // Validate stored value
      if (stored === 'split' || stored === 'unified') {
        return stored;
      }
    } catch (error) {
      // Fail silently - sessionStorage may be unavailable in private browsing
      console.debug('sessionStorage unavailable:', error);
    }
    return defaultMode;
  });

  // Sync changes to sessionStorage
  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, viewMode);
    } catch (error) {
      // Fail silently - just fall back to in-memory state
      console.debug('Failed to persist view mode:', error);
    }
  }, [viewMode]);

  return [viewMode, setViewMode];
}
